import { CartItemTypeEnum, type CartItem } from '../models/cart.model';
import type {
    CartItemInterface,
    CartItemPrices,
    ConfigurableCartItem,
    ConfigurableProduct,
    ConfigurableVariant,
    CartPrices,
    Money,
    Discount,
    CurrencyEnum,
    Maybe,
} from '../types/front.graphql';
import type { CartItemReference } from '../models';

export type UpdateItemInput = Partial<Omit<CartItemInterface, 'id'>>;

// ============================================================================
// UNIT PRICE RESOLUTION
// ============================================================================

/**
 * Get the effective unit price for a cart item.
 *
 * Resolution order (Magento price waterfall):
 *  1. `prices.price.value` — unit price from Magento cart (post catalog-rule / special price)
 *  2. configured_variant `final_price` — for configurable items (variant-specific)
 *  3. product `final_price` — product-level final price
 *  4. product `regular_price` — original price before any discounts
 *
 * This is the single source of truth for local price calculations.
 * Used by updateCartItemQuantityAndPrices and subtotal calculators.
 */
export const getItemEffectiveUnitPrice = (item: CartItemInterface): number => {
    // Best: cart-level unit price (post-discount)
    if (item.prices?.price?.value != null && item.prices.price.value > 0) {
        // TODO-3.2: Staleness check — verify that prices.price.value is consistent
        // with row_total. After reconciliation with pre-collectTotals server data,
        // prices.price.value may be stale (reflecting old catalog rules).
        // If prices.price.value × quantity doesn't match row_total within tolerance,
        // fall through to final_price from the product catalog.
        const qty = item.quantity ?? 1;
        const expectedRowTotal = item.prices.price.value * qty;
        const actualRowTotal = item.prices?.row_total?.value;

        if (actualRowTotal != null && actualRowTotal > 0 && qty > 0) {
            const tolerance = Math.max(expectedRowTotal * 0.005, 1); // 0.5% or 1 currency unit
            if (Math.abs(expectedRowTotal - actualRowTotal) > tolerance) {
                // prices.price.value is stale — fall through to catalog final_price
                const configurableItem = item as Partial<ConfigurableCartItem>;
                const variantFinalPrice =
                    configurableItem?.configured_variant?.price_range?.minimum_price?.final_price?.value;
                if (variantFinalPrice != null && variantFinalPrice > 0) {
                    return variantFinalPrice;
                }
                const finalPrice = item.product?.price_range?.minimum_price?.final_price?.value;
                if (finalPrice != null && finalPrice > 0) {
                    return finalPrice;
                }
                // If no catalog price available, still use prices.price.value
                // (better than returning 0)
            }
        }

        return item.prices.price.value;
    }
    // Fallback: product final_price (includes catalog rules, special prices)
    const configurableItem = item as Partial<ConfigurableCartItem>;
    const variantFinalPrice = configurableItem?.configured_variant?.price_range?.minimum_price?.final_price?.value;
    if (variantFinalPrice != null && variantFinalPrice > 0) {
        return variantFinalPrice;
    }
    const finalPrice = item.product?.price_range?.minimum_price?.final_price?.value;
    if (finalPrice != null && finalPrice > 0) {
        return finalPrice;
    }
    // Last resort: regular price
    return item.product?.price_range?.minimum_price?.regular_price?.value ?? 0;
};

/**
 * @deprecated Use `updateCartItemQuantityAndPrices` instead, which derives
 * the row total from the authoritative unit price on the product/variant.
 *
 * This ratio-based approach (`row_total / currentQty * newQty`) is unreliable:
 *  - `row_total` can be stale after server reconciliation
 *  - Rounding errors compound on repeated quantity changes
 *  - Tier pricing / catalog rules make the ratio invalid
 */
export const calculateCartItemRowTotal = (value: number, currentQuantity: number, newQuantity: number): number => {
    currentQuantity = Number(currentQuantity);

    if (currentQuantity === 0 || Number.isNaN(currentQuantity)) {
        return value * newQuantity;
    }
    return (value / currentQuantity) * newQuantity;
};

/**
 * Update cart item quantity and recalculate prices (row_total, row_total_including_tax).
 *
 * Price derivation strategy (in priority order):
 *  1. `prices.price.value` — unit price from Magento cart (post catalog-rule / special price)
 *  2. configured_variant `final_price` — for configurable items (variant-specific)
 *  3. product `final_price` — product-level final price
 *  4. product `regular_price` — original price before any discounts
 *
 * This avoids the ratio-based `row_total / oldQty * newQty` approach which is
 * prone to stale data after reconciliation and rounding errors.
 *
 * NOTE: Item-level discounts are intentionally stripped because they were
 * server-computed for the old quantity and become invalid after a local qty change.
 * Accurate discounts arrive via FastCartTotals from the server.
 *
 * CRITICAL: Must be called whenever quantity changes to keep prices in sync.
 * @param item - Cart item to update
 * @param updatedQuantity - New quantity
 * @returns Updated cart item with recalculated prices
 */
export const updateCartItemQuantityAndPrices = (
    item: CartItemInterface,
    updatedQuantity: number
): CartItemInterface => {
    const unitPrice = getItemEffectiveUnitPrice(item);
    const newRowTotal = unitPrice * updatedQuantity;

    const rowTotal = item?.prices?.row_total;
    const rowTotalIncludingTax = item?.prices?.row_total_including_tax;

    const updatedPrices: CartItemPrices = {
        ...(item?.prices as CartItemPrices),
        price: {
            ...item?.prices?.price,
            value: unitPrice,
        } as Money,
        row_total: {
            ...rowTotal,
            value: newRowTotal,
        } as Money,
        row_total_including_tax: {
            ...rowTotalIncludingTax,
            // For VN market prices include VAT, so row_total ≈ row_total_including_tax.
            // For markets with separate tax, the server will provide the accurate value.
            value: newRowTotal,
        } as Money,
        // Strip stale discount data — these were computed by the server for the
        // old quantity and are invalid after a local qty change. Accurate discounts
        // will arrive from FastCartTotals.
        discounts: null,
        total_item_discount: { __typename: 'Money', value: 0, currency: rowTotal?.currency } as Money,
    };

    return {
        ...item,
        quantity: updatedQuantity,
        prices: updatedPrices,
    };
};

export function getCartItemVariation(item: ConfigurableCartItem): ConfigurableVariant | undefined {
    // get the value array of selected configurable options and filter null or undefined value
    const selectedConfigurableOptionValue = item.configurable_options
        .map(option => option?.value_id)
        .filter(value => !!value);
    const configurableProduct = item.product as ConfigurableProduct;
    if (!configurableProduct?.variants) {
        return undefined;
    }
    for (const variant of configurableProduct.variants) {
        if (!variant) {
            continue;
        }

        let matchOptionValueCount = 0;
        variant?.attributes?.forEach(attribute => {
            if (selectedConfigurableOptionValue.includes(attribute?.value_index ?? 0)) {
                matchOptionValueCount++;
            }
        });

        if (matchOptionValueCount === selectedConfigurableOptionValue.length) {
            return variant;
        }
    }
    return undefined;
}

export function isConfigurableCartItemExisting(item: ConfigurableCartItem, cartItem: CartItemInterface) {
    const matchVariation = getCartItemVariation(item);
    const isMatchVariation = matchVariation?.product?.id === cartItem.product.id;
    const isMatchCartItemId = item.id === cartItem.id;
    return isMatchVariation || isMatchCartItemId;
}
export function addItemWithQuantity(items: CartItem[], item: CartItemInterface, quantity: number) {
    if (quantity <= 0) throw new Error("cartQuantity can't be zero or less than zero");
    const existingItemIndex = items.findIndex(currentItem => {
        if (
            currentItem.__typename === CartItemTypeEnum.CONFIGURABLE ||
            currentItem.__typename === CartItemTypeEnum.CONFIGURABLE_LOCAL
        ) {
            return isConfigurableCartItemExisting((currentItem ?? {}) as ConfigurableCartItem, item);
        }
        return currentItem.product.id === item.product.id;
    });

    if (existingItemIndex > -1) {
        const newItems = [...items];
        const currentItem = newItems[existingItemIndex];
        const newQuantity = currentItem.quantity + quantity;
        newItems[existingItemIndex] = updateCartItemQuantityAndPrices(currentItem, newQuantity);
        return newItems;
    }
    const newCartItem = updateCartItemQuantityAndPrices({ ...item }, quantity);
    return [...items, newCartItem];
}

export function removeItemOrQuantity(items: CartItemInterface[], id: CartItemInterface['id'], quantity: number) {
    return items.reduce((acc: CartItemInterface[], item) => {
        if (item.id === id) {
            const newQuantity = item.quantity - quantity;
            const updatedCartItem = updateCartItemQuantityAndPrices(item, newQuantity);
            return newQuantity > 0 ? [...acc, { ...updatedCartItem }] : [...acc];
        }
        return [...acc, item];
    }, []);
}

export function updateItemWithQuantity(items: CartItemInterface[], id: CartItemInterface['id'], quantity: number) {
    const existingItemIndex = items.findIndex(item => item.id === id);
    if (existingItemIndex === -1) {
        throw new Error('Cart item does not exist');
    }
    const newItems = [...items];
    const currentItem = newItems[existingItemIndex];
    newItems[existingItemIndex] = updateCartItemQuantityAndPrices(currentItem, quantity);
    return newItems;
}
// Simple CRUD for Item
export function addItem(items: CartItemInterface[], item: CartItemInterface) {
    return [...items, item];
}

export function getItem(items: CartItemInterface[], id: CartItemInterface['id']) {
    if (!items) return undefined;
    return items.find(item => item?.id === id);
}

export function updateItem(items: CartItemInterface[], id: CartItemInterface['id'], item: UpdateItemInput) {
    return items.map(existingItem => (existingItem.id === id ? { ...existingItem, ...item } : existingItem));
}

export function removeItem(items: CartItemInterface[], id: CartItemInterface['id']) {
    return items.filter(existingItem => existingItem.id !== id);
}
export function inStock(items: CartItemInterface[], id: CartItemInterface['id']) {
    const item = getItem(items, id);
    if (item) return item.quantity < 2; // @TODO: should fetch from backend
    return false;
}
export const calculateItemTotals = (items: CartItemInterface[]) =>
    items.map(item => ({
        ...item,
        itemTotal: item.prices?.row_total?.value || 0,
    }));

export const calculateTotal = (items: CartItemInterface[]) =>
    items.reduce((total, item) => total + (item.prices?.row_total?.value || 0), 0);

export const calculateTotalItems = (items: CartItemInterface[]) => items?.reduce((sum, item) => sum + item.quantity, 0);

export const calculateUniqueItems = (items: CartItemInterface[]) => items.length;

interface PriceValues {
    totalAmount: number;
    tax: number;
    shipping_charge: number;
}
export const calculatePaidTotal = ({ totalAmount, tax, shipping_charge }: PriceValues, discount?: number) => {
    let paidTotal = totalAmount + tax + shipping_charge;
    if (discount) {
        paidTotal = paidTotal - discount;
    }
    return paidTotal;
};

export function getItemByProduct(items: CartItem[], cartItem: CartItemInterface) {
    if (!items) return undefined;

    return items.find(item => {
        if (
            item.__typename === CartItemTypeEnum.CONFIGURABLE ||
            item.__typename === CartItemTypeEnum.CONFIGURABLE_LOCAL
        ) {
            const isNewCartItem = item.id.startsWith('new-');
            const isExisting = isConfigurableCartItemExisting((item ?? {}) as ConfigurableCartItem, cartItem);
            return isExisting || isNewCartItem;
        }
        return item?.product.id === cartItem.product.id;
    });
}

export function groupCartItemsByProductId(cartItems: CartItemInterface[]): Record<string, CartItemInterface[]> {
    return cartItems.reduce<Record<string, CartItemInterface[]>>((acc, item) => {
        if (!item?.product?.id) return acc;
        const key = String(item.product.id);
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {});
}

// ============================================================================
// CART PRICES CALCULATION - Local computation from cart items
// ============================================================================

/**
 * Calculate subtotal excluding tax from cart items
 * Sum of all items' row_total (price * quantity without tax)
 */
export const calculateSubtotalExcludingTax = (items: CartItemInterface[]): number => {
    return items.reduce((total, item) => {
        // Always derive from unitPrice × qty instead of trusting stored row_total.
        // Stored row_total can be stale after reconcileWithServerReferences
        // overwrites with pre-collectTotals server data.
        const rowTotal = getItemEffectiveUnitPrice(item) * (item.quantity ?? 0);
        return total + rowTotal;
    }, 0);
};

/**
 * Calculate subtotal including tax from cart items.
 * Sum of all items' row_total_including_tax (price * quantity with tax).
 *
 * Uses row_total_including_tax from server data when available and non-stale
 * (i.e., stored row_total qty matches current item qty).
 * Falls back to getItemEffectiveUnitPrice × qty otherwise.
 *
 * NOTE (Vietnam market): VN product prices already include VAT, so in practice
 * this returns the same value as calculateSubtotalExcludingTax. The distinction
 * matters for regions with separate tax computation.
 */
export const calculateSubtotalIncludingTax = (items: CartItemInterface[]): number => {
    return items.reduce((total, item) => {
        const qty = item.quantity ?? 0;
        // Prefer server-provided row_total_including_tax when it aligns with
        // the current quantity (non-stale). If the stored value was computed
        // for a different qty (e.g., after reconciliation with outdated server
        // data), it would be wrong — fall back to unit-price derivation.
        const serverRowTotalIncTax = item.prices?.row_total_including_tax?.value;
        if (serverRowTotalIncTax != null && serverRowTotalIncTax > 0) {
            // Validate staleness: server row_total should be proportional to qty
            const unitPrice = getItemEffectiveUnitPrice(item);
            const expectedTotal = unitPrice * qty;
            // Allow small rounding tolerance (0.5% or 1 currency unit)
            const tolerance = Math.max(expectedTotal * 0.005, 1);
            if (Math.abs(serverRowTotalIncTax - expectedTotal) <= tolerance) {
                return total + serverRowTotalIncTax;
            }
        }
        // Fallback: derive from unit price × qty (same as excluding-tax for VN)
        const rowTotalWithTax = getItemEffectiveUnitPrice(item) * qty;
        return total + rowTotalWithTax;
    }, 0);
};

/**
 * Calculate total item discounts from cart items
 * Sum of all items' total_item_discount
 */
export const calculateTotalItemDiscounts = (items: CartItemInterface[]): number => {
    return items.reduce((total, item) => {
        const itemDiscount = item.prices?.total_item_discount?.value ?? 0;
        return total + itemDiscount;
    }, 0);
};

/**
 * Collect all discounts from cart items
 * Aggregates discount information from all items
 */
export const collectItemDiscounts = (items: CartItemInterface[]): Discount[] => {
    const discountMap = new Map<string, Discount>();

    items.forEach(item => {
        const itemDiscounts = item.prices?.discounts ?? [];
        itemDiscounts.forEach(discount => {
            if (!discount) return;

            const key = discount.label;
            const existingDiscount = discountMap.get(key);

            if (existingDiscount) {
                // Aggregate discount amount
                const currentAmount = existingDiscount.amount?.value ?? 0;
                const newAmount = discount.amount?.value ?? 0;
                discountMap.set(key, {
                    ...existingDiscount,
                    amount: {
                        ...existingDiscount.amount,
                        value: currentAmount + newAmount,
                    },
                });
            } else {
                discountMap.set(key, discount);
            }
        });
    });

    return Array.from(discountMap.values());
};

/**
 * Calculate subtotal with discount excluding tax
 * subtotal_excluding_tax - total_item_discount
 */
export const calculateSubtotalWithDiscountExcludingTax = (items: CartItemInterface[]): number => {
    const subtotal = calculateSubtotalExcludingTax(items);
    const totalDiscount = calculateTotalItemDiscounts(items);
    return subtotal - totalDiscount;
};

/**
 * Create Money object from value
 */
const createMoneyObject = (value: number, currency: CurrencyEnum = 'VND' as CurrencyEnum): Money => ({
    __typename: 'Money',
    value,
    currency,
});

/**
 * Calculate complete cart prices from cart items
 * This replicates the CartPrices structure from Magento GraphQL
 *
 * @param items - Array of cart items
 * @param currency - Currency code (default: VND)
 * @param cartLevelDiscounts - Optional cart-level discounts (coupons, promotions)
 * @returns CartPrices object matching Magento structure
 */
export const calculateCartPricesFromItems = (
    items: CartItemInterface[],
    currency: CurrencyEnum = 'VND' as CurrencyEnum,
    cartLevelDiscounts?: Maybe<Array<Maybe<Discount>>>
): CartPrices => {
    // Calculate base values
    const subtotalExcludingTax = calculateSubtotalExcludingTax(items);
    const subtotalIncludingTax = calculateSubtotalIncludingTax(items);
    const subtotalWithDiscountExcludingTax = calculateSubtotalWithDiscountExcludingTax(items);

    // Collect all discounts (item-level + cart-level)
    const itemDiscounts = collectItemDiscounts(items);
    const allDiscounts = cartLevelDiscounts ? [...itemDiscounts, ...cartLevelDiscounts] : itemDiscounts;

    // Calculate total discount amount
    const totalDiscountAmount = allDiscounts.reduce((total, discount) => {
        return total + (discount?.amount?.value ?? 0);
    }, 0);

    // Calculate grand total (subtotal with tax - discounts)
    // Note: In Magento, grand_total = subtotal_including_tax - discounts + shipping + tax adjustments
    // For local calculation without shipping, we use: subtotal_including_tax - discounts
    const grandTotal = subtotalIncludingTax - totalDiscountAmount;

    return {
        __typename: 'CartPrices',
        subtotal_excluding_tax: createMoneyObject(subtotalExcludingTax, currency),
        subtotal_including_tax: createMoneyObject(subtotalIncludingTax, currency),
        subtotal_with_discount_excluding_tax: createMoneyObject(subtotalWithDiscountExcludingTax, currency),
        grand_total: createMoneyObject(grandTotal, currency),
        discounts: allDiscounts.length > 0 ? allDiscounts : null,
        applied_taxes: null, // Tax calculation requires server-side logic
    };
};

/**
 * Calculate cart prices for selected items only
 * Useful when calculating totals for checked items in cart
 *
 * @param items - Array of cart items
 * @param selectedItemIds - Array of selected item IDs
 * @param currency - Currency code
 * @param cartLevelDiscounts - Optional cart-level discounts
 * @returns CartPrices for selected items only
 */
export const calculateSelectedItemsPrices = (
    items: CartItemInterface[],
    selectedItemIds: string[],
    currency: CurrencyEnum = 'VND' as CurrencyEnum,
    cartLevelDiscounts?: Maybe<Array<Maybe<Discount>>>
): CartPrices => {
    const selectedItems = items.filter(item => selectedItemIds.includes(item.id));
    return calculateCartPricesFromItems(selectedItems, currency, cartLevelDiscounts);
};

// ============================================================================
// CART ITEM REFERENCE PRICES CALCULATION
// ============================================================================

/**
 * Calculate cart prices from selected cart item references
 * IMPORTANT: This function calculates prices ONLY for selected cart item references
 *
 * Key differences from calculateSelectedItemsPrices:
 * - Works with CartItemReference[] instead of CartItemInterface[]
 * - Filters by isSelected flag instead of selectedItemIds array
 * - Extracts CartItemInterface from cartItem within each reference
 * - Only includes item-level discounts (cart-level discounts handled by server)
 *
 * @param references - Array of cart item references
 * @param currency - Currency code (default: VND)
 * @returns CartPrices for selected references only
 */
export const calculateSelectedReferencesPrices = (
    references: CartItemReference[],
    currency: CurrencyEnum = 'VND' as CurrencyEnum
): CartPrices => {
    // Filter only selected references
    const selectedReferences = references.filter(ref => ref.isSelected === true);

    if (selectedReferences.length === 0) {
        return {
            __typename: 'CartPrices',
            subtotal_excluding_tax: createMoneyObject(0, currency),
            subtotal_including_tax: createMoneyObject(0, currency),
            subtotal_with_discount_excluding_tax: createMoneyObject(0, currency),
            grand_total: createMoneyObject(0, currency),
            discounts: null,
            applied_taxes: null,
        };
    }

    // Extract cart items from selected references.
    // CRITICAL: Normalize quantity from ref.qty (local truth) and strip stale
    // server discount data. After reconciliation, cartItem may carry pre-collectTotals
    // prices where discounts were computed for a different qty. Keeping them would
    // produce wildly wrong grandTotal (e.g. subtotal - stale_discount = garbage).
    // Local grandTotal = subtotal (no discounts). Accurate discounts come from
    // the server via FastCartTotals.
    const cartItems: CartItemInterface[] = [];

    selectedReferences.forEach(ref => {
        if (ref.cartItem) {
            cartItems.push({
                ...(ref.cartItem as CartItemInterface),
                quantity: ref.qty, // Use ref.qty as source of truth
                prices: {
                    ...((ref.cartItem as CartItemInterface).prices as CartItemPrices),
                    discounts: null, // Strip stale discount data
                    total_item_discount: { __typename: 'Money', value: 0, currency },
                },
            });
        }
    });

    if (cartItems.length === 0) {
        console.warn('[calculateSelectedReferencesPrices] No cart items found in selected references');
        return {
            __typename: 'CartPrices',
            subtotal_excluding_tax: createMoneyObject(0, currency),
            subtotal_including_tax: createMoneyObject(0, currency),
            subtotal_with_discount_excluding_tax: createMoneyObject(0, currency),
            grand_total: createMoneyObject(0, currency),
            discounts: null,
            applied_taxes: null,
        };
    }

    // Calculate prices from cart items (item-level discounts stripped above,
    // so grandTotal = subtotal). Server prices from FastCartTotals will replace
    // this with accurate discounts.
    const prices = calculateCartPricesFromItems(cartItems, currency, undefined);

    // TODO-1.1: Explicitly set subtotal_with_discount = subtotal when no discounts.
    // calculateCartPricesFromItems already computes this correctly via
    // calculateSubtotalWithDiscountExcludingTax (subtotal - 0 = subtotal), but
    // we make it explicit here for clarity: local calculation has NO cart-level
    // discounts, so subtotal_with_discount must equal subtotal.
    if (!prices.discounts || prices.discounts.length === 0) {
        prices.subtotal_with_discount_excluding_tax = {
            ...prices.subtotal_with_discount_excluding_tax!,
            value: prices.subtotal_excluding_tax?.value ?? 0,
        };
    }

    return prices;
};

export const getCartItemSku = (item: CartItemInterface): string | undefined => {
    const configurableItem = item as Partial<ConfigurableCartItem>;

    if (configurableItem.configured_variant?.sku != null) {
        return configurableItem.configured_variant.sku ?? undefined;
    }
    return item.product?.sku ?? undefined;
};

/**
 * Get the original unit price (regular_price) for a cart item BEFORE any discounts.
 *
 * Resolution order:
 * 1. For configurable items: configured_variant's regular_price (variant-specific)
 * 2. Product's regular_price (parent product)
 * 3. Fallback: prices.price.value (unit price, may already be discounted by catalog rules)
 */
export const getItemOriginalUnitPrice = (item: CartItemInterface): number => {
    const configurableItem = item as Partial<ConfigurableCartItem>;

    // For configurable items, prefer the selected variant's regular price
    const variantRegularPrice = configurableItem?.configured_variant?.price_range?.minimum_price?.regular_price?.value;
    if (variantRegularPrice != null && variantRegularPrice > 0) {
        return variantRegularPrice;
    }

    // For simple items or when variant price is unavailable
    const regularPrice = item.product?.price_range?.minimum_price?.regular_price?.value;
    if (regularPrice != null && regularPrice > 0) {
        return regularPrice;
    }

    // Fallback to unit price (already discounted by catalog rules)
    return item.prices?.price?.value ?? 0;
};

/**
 * TODO-1.3: Calculate original total (before any discounts) from selected cart items.
 * Shared utility used by both CartTotal and CartInformation for consistent calculation.
 *
 * Filters by stock availability (isSalable) to exclude out-of-stock items.
 * Uses getItemOriginalUnitPrice (regular_price) × quantity for each item.
 *
 * @param items - Array of cart items (selected items only)
 * @param stockAvailabilityMap - Map of SKU → { isSalable: boolean } from stock status store
 * @returns Total original price before any discounts
 */
export const calculateOriginalTotalFromItems = (
    items: CartItemInterface[],
    stockAvailabilityMap?: Record<string, { isSalable?: boolean }>
): number => {
    let total = 0;
    for (const item of items) {
        const sku = item.product?.sku ?? '';
        // Skip out-of-stock items when stock map is provided
        if (stockAvailabilityMap && stockAvailabilityMap[sku]?.isSalable === false) continue;
        total += getItemOriginalUnitPrice(item) * item.quantity;
    }
    return total;
};
